/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useState, useRef, useEffect } from 'react';
import classnames from 'classnames';
import styles from './Select.module.scss';
import ArrowDown from 'app/assets/Icons/ArrowDown.svg';
import { ISelect, SelectOption } from './Select.interface';

export const Select: React.FC<ISelect> = ({
  label,
  options,
  selectedValue,
  onSelect,
  searchable = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  const selectedOption = options.find(
    (option) => option.value === selectedValue
  );
  const displayValue = selectedOption ? selectedOption.label : '';
  const hasValue = !!selectedValue;

  const filteredOptions = searchable
    ? options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOptionClick = (option: SelectOption): void => {
    onSelect(option);
    setIsOpen(false);
    setSearchTerm('');
  };

  const toggleDropdown = (): void => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSearchTerm('');
    }
  };

  return (
    <div ref={dropdownRef} className={classnames(styles.select)}>
      <button
        type="button"
        className={styles.trigger}
        onClick={toggleDropdown}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            setIsOpen(false);
          }
        }}
        aria-expanded={isOpen}
        aria-label={`${label} selector`}
      >
        <div className={styles['input-container']}>
          <span
            className={classnames(styles.label, {
              [styles.floating]: hasValue
            })}
          >
            {label}
          </span>
          {hasValue && <span className={styles.value}>{displayValue}</span>}
        </div>
        <img
          src={ArrowDown}
          alt="Toggle dropdown"
          className={classnames(styles.arrow, { [styles.open]: isOpen })}
        />
      </button>

      {isOpen && (
        <div className={styles.dropdown}>
          {searchable && (
            <div className={styles['search-container']}>
              <input
                type="text"
                placeholder="Search"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles['search-input']}
              />
            </div>
          )}
          <div className={styles.options}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => (
                <button
                  type="button"
                  key={option.value}
                  className={classnames(styles.option, {
                    [styles.selected]: option.value === selectedValue
                  })}
                  aria-pressed={option.value === selectedValue}
                  onClick={() => handleOptionClick(option)}
                >
                  {option.label}
                </button>
              ))
            ) : (
              <div className={styles['no-options']}>No options found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
